import { CanvasCommand, CreateElementCommand } from "../../command";
import { svgObjectURL } from "../../Elements/factory";
import { ILayout, IllustrationElement, ImageElement, LayoutElementType, Region, RegionElement, SilentStepData, Step, StepType } from "../../types";
import { Product } from "../../types";
import { fetchAsString } from "../../util/crossplatform";
import { LayoutNotFoundError, MisconfigurationError } from "../../util/exception";
import { generate } from "../../util/guid";
import { generateSVGWithUnknownColors } from "../../util/illustration";

export interface SilentStepTriggerResult {
    regionElement: RegionElement;
    command: CanvasCommand;
}

export class SilentStepHandler {
    private configuration: Step<SilentStepData>;
    private layouts: ILayout[];
    private product?: Product;

    constructor(config: Step<SilentStepData>, layouts: ILayout[], product?: Product) {
        this.configuration = config;
        this.layouts = layouts;
        this.product = product ? product : undefined;
    }

    async trigger(): Promise<SilentStepTriggerResult[]> {
        if (!this.configuration.data.regions) {
            throw new MisconfigurationError(this.configuration, "Missing regions.");
        }
        if (this.configuration.type === StepType.SilentIllustration) {
            const promises = this.configuration.data.regions.map(this.processRegion);
            return Promise.all(promises);
        } else if (this.configuration.type === StepType.ProductOverlay) {
            return Promise.all(
                this.configuration.data.regions.map((region) => {
                    const layout = this.layouts.find((layout: ILayout) => region.panelId === layout.panelId);
                    if (!layout) {
                        throw new LayoutNotFoundError(region);
                    }
                    return this.processRegion(region);
                }),
            );
        } else {
            return Promise.reject("Unknown silent step. This is a bug");
        }
    }

    private processRegion = async (region: Region): Promise<SilentStepTriggerResult> => {
        const layout = this.layouts.find((layout: ILayout) => layout.panelId === region.panelId);
        if (!layout) {
            throw new LayoutNotFoundError(region);
        }

        let assetUrl = "";
        if (this.configuration.type === StepType.ProductOverlay) {
            // Check related product for a overlay key
            let assetFromProduct = "";
            if (this.product?.overlayImageUrl) {
                assetFromProduct = this.product.overlayImageUrl;
            }
            assetUrl = assetFromProduct;
            if (!assetFromProduct) {
                throw new MisconfigurationError(this.configuration, "Couldn't find an asset for product overlay step");
            }
        }

        const assetType = this.evaluateAssetType();

        if (assetType === LayoutElementType.Image) {
            const assetUrl =
                this.configuration.type === StepType.SilentIllustration
                    ? (this.configuration.data.asset?.fileLink)
                    : (this.product?.overlayImageUrl);
            if (!assetUrl) {
                throw new Error("Undefined raster silent step source");
            }
            const newElement: ImageElement = {
                stepName: this.configuration.stepName,
                id: generate(),
                src: assetUrl,
                type: assetType,
                y: region.top,
                x: region.left,
                width: region.width,
                height: region.height,
                layer: region.layer,
                layerIndex: region.layerIndex,
                immutable: region.immutable,
                productOverlay: this.configuration.type === StepType.ProductOverlay ? true : undefined,
                rotation: region.rotation,
                excludeFromExport: this.configuration.data.excludeFromPrint,
                preserveAspectRatio: "none",
            };
            return {
                regionElement: {
                    id: newElement.id,
                    region,
                },
                command: new CreateElementCommand<ImageElement>(newElement, layout),
            };
        } else {
            const src =
                this.configuration.type === StepType.SilentIllustration ? this.configuration.data.asset?.fileLink : assetUrl;
            const getIllustrationBody = async (): Promise<string> => {
                return new Promise((resolve, reject) => {
                    if (!src) {
                        reject("Undefined vector silent step source");
                        return;
                    }
                    fetchAsString(src, true)
                        .then((svg) => {
                            resolve(svg);
                        })
                        .catch((e) => console.error(e));
                });
            };
            const svgData = await generateSVGWithUnknownColors(await getIllustrationBody());
            const newElement: IllustrationElement = {
                stepName: this.configuration.stepName,
                id: generate(),
                cachedObjectURL: await svgObjectURL(svgData.svg),
                src: src,
                svg: svgData.svg,
                colors: svgData.colors,
                type: assetType,
                y: region.top,
                x: region.left,
                width: region.width,
                height: region.height,
                layer: region.layer,
                layerIndex: region.layerIndex,
                rotation: region.rotation,
                immutable: region.immutable,
                productOverlay: this.configuration.type === StepType.ProductOverlay ? true : undefined,
                excludeFromExport: this.configuration.data.excludeFromPrint,
            };
            return {
                regionElement: {
                    id: newElement.id!,
                    region,
                },
                command: new CreateElementCommand<IllustrationElement>(newElement, layout),
            };
        }
    };

    private evaluateAssetType(): LayoutElementType {
        // Try to determine the type by asset path.
        const url =
            this.configuration.type === StepType.ProductOverlay && this.product?.overlayImageUrl
                ? this.product.overlayImageUrl.toLowerCase()
                : this.configuration.data.asset?.fileLink?.toLowerCase().split("?")[0];
        if (
            url?.startsWith("data:image/png") ||
            url?.endsWith(".jpeg") ||
            url?.endsWith(".jpg") ||
            url?.endsWith(".png")
        ) {
            return LayoutElementType.Image;
        }
        if (url?.endsWith(".svg") || url?.startsWith("image/svg+xml")) {
            return LayoutElementType.Illustration;
        }
        return LayoutElementType.Illustration;
    }
}
